import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
} from '@angular/core/rxjs-interop';
import { SimplifiedUser } from '@galaxy/crm/static';
import { FieldValueInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import {
  BlankValueComponent,
  CustomCellComponent,
} from '@vendasta/galaxy/table';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CRMUserService } from '../../user.service';
import { SystemFieldIds } from '@galaxy/crm/services/field-service';

const NO_VALUE_FOR_TEAM_MEMBER = '<NO_VALUE_FOR_TEAM_MEMBER>';
const AVATAR_WIDTH = 24;

@Component({
  template: `
    <ng-container *ngIf="teamMember() as teamMember; else loading">
      <div class="team-member" *ngIf="teamMember !== NO_VALUE_FOR_TEAM_MEMBER; else blank">
        <span>{{ teamMemberName() }}</span>
      </div>
    </ng-container>
    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  styleUrls: ['./owner.component.scss'],
  imports: [CommonModule, BlankValueComponent, GalaxyAvatarModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerCellComponent extends CustomCellComponent {
  AVATAR_WIDTH = AVATAR_WIDTH;
  NO_VALUE_FOR_TEAM_MEMBER = NO_VALUE_FOR_TEAM_MEMBER;
  fieldValues = signal([]);
  set value(newValues: (FieldValueInterface | null)[]) {
    this.fieldValues.set(newValues);
  }
  teamMember: WritableSignal<SimplifiedUser | '<NO_VALUE_FOR_TEAM_MEMBER>' | null> = signal(null);
  teamMemberName = computed(() => {
    const teamMember = this.teamMember();
    if (teamMember === NO_VALUE_FOR_TEAM_MEMBER) {
      return '';
    }
    return teamMember?.displayName ?? '';
  });

  constructor(private readonly userService: CRMUserService) {
    super();
    toObservable(this.fieldValues)
      .pipe(
        switchMap((fieldValues) => {
          const userId =
            fieldValues.find(
              (fieldValue) =>
                fieldValue?.fieldId === SystemFieldIds.CompanyOwnerID ||
                fieldValue?.fieldId === SystemFieldIds.ContactOwnerID,
            ) ?? null;
          if (!userId || !userId?.stringValue) {
            return of(NO_VALUE_FOR_TEAM_MEMBER);
          }
          return this.userService.getMultiUsers([userId.stringValue]);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        if (result === NO_VALUE_FOR_TEAM_MEMBER || !result.length) {
          this.teamMember.set(NO_VALUE_FOR_TEAM_MEMBER);
          return;
        }
        const teamMember = result[0] as SimplifiedUser;
        if (!teamMember?.userId) {
          this.teamMember.set(NO_VALUE_FOR_TEAM_MEMBER);
          return;
        }
        this.teamMember.set(teamMember);
      });
  }
}
